'use client';

import { c as _c } from "react/compiler-runtime";
import type { Command } from '@/commands/types';
import { MessageSquareDot, MessageSquareWarning, Wrench } from 'lucide-react';
import { useRef } from 'react';
import type { PrV2CommentThreadRow, PrV2CommentThreads } from './use-pr-v2-comment-threads';
import type { PrV2FixAll } from './use-pr-v2-fix-all';
import { isDisplayedCubicLogin } from './pr-v2-cubic-review-utils';
import { getThreadLocation, getThreadPriority } from './use-pr-v2-comment-threads';
import { confirmCommandAction } from '@/commands/confirm-command-action';
import { prV2Command, prV2ModeRow } from '@/commands/modules/pr-v2-command-group';
import { useRegisterCommands } from '@/commands/use-commands';
import { splitFilePath } from '@/lib/file-paths/split-file-path';

/**
 * A "Go to finding..." row: "P1: preview", then the file and line, muted, as the comments popover
 * shows them. A full path doesn't fit beside the preview, so it is a keyword instead.
 */
function buildFindingRow(row: PrV2CommentThreadRow, goToComment: (commentId: string) => void): Command {
  const priority = getThreadPriority(row);
  const label = priority ? `${priority}: ${row.preview}` : row.preview;
  const fileName = row.path ? splitFilePath(row.path).basename : undefined;
  const location = getThreadLocation(row);
  return prV2ModeRow({
    id: `pr-v2-finding:${row.threadId}`,
    tag: 'pr-v2-finding',
    label,
    displayLabel: location ? <span className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 truncate">{label}</span>
        <span className="shrink-0 text-zinc-500 group-data-[selected=true]:text-zinc-300">
          {location}
        </span>
      </span> : undefined,
    keywords: [row.path, fileName, row.sourceLabel].filter((keyword): keyword is string => Boolean(keyword)),
    checked: row.status === 'resolved',
    action: () => goToComment(row.githubCommentId)
  });
}

/**
 * The open thread after the last one opened, wrapping around. It starts from the first open thread
 * when the last one is unknown or no longer open, as resolving a thread takes it off the open list.
 * Null when no thread is open.
 */
function getNextOpenThread(rows: readonly PrV2CommentThreadRow[], lastThreadId: string | null): PrV2CommentThreadRow | null {
  const openRows = rows.filter(row => row.status === 'open');
  const lastIndex = openRows.findIndex(row => row.threadId === lastThreadId);
  return openRows[(lastIndex + 1) % openRows.length] ?? null;
}
export function usePrV2CommentCommands(t0) {
  const $ = _c(15);
  const {
    threads,
    goToComment,
    fixAll
  } = t0;
  const lastOpenedThreadIdRef = useRef(null);
  let t1;
  if ($[0] !== fixAll || $[1] !== goToComment || $[2] !== threads.botThreadRows || $[3] !== threads.humanThreadRows) {
    t1 = () => {
      const cubicThreadRows = threads.botThreadRows.filter(_temp);
      const threadRows = [...threads.botThreadRows, ...threads.humanThreadRows];
      const commands = cubicThreadRows.length > 0 ? [prV2Command("pr-v2-go-to-finding", {
        label: "Go to finding...",
        icon: <MessageSquareWarning />,
        keywords: ["cubic", "issues", "review"],
        enterMode: "pr-v2-findings",
        action: _temp2
      }), ...cubicThreadRows.map(row_0 => buildFindingRow(row_0, goToComment))] : [];
      if (threadRows.some(_temp3)) {
        commands.push(prV2Command("pr-v2-next-unresolved-thread", {
          label: "Next unresolved thread",
          icon: <MessageSquareDot />,
          keywords: ["comment", "open", "review"],
          action: () => {
            const nextRow = getNextOpenThread(threadRows, lastOpenedThreadIdRef.current);
            if (nextRow) {
              lastOpenedThreadIdRef.current = nextRow.threadId;
              goToComment(nextRow.githubCommentId);
            }
          }
        }));
      }
      if (fixAll.state === "ready" && fixAll.isProviderResolved && !fixAll.isSubmitting) {
        commands.push(prV2Command("pr-v2-fix-all", {
          label: fixAll.label,
          icon: <Wrench />,
          keywords: ["cubic", "comments", "issues"],
          action: () => confirmCommandAction(`${fixAll.description}?`, () => {
            fixAll.run();
          })
        }));
      }
      return commands;
    };
    $[0] = fixAll;
    $[1] = goToComment;
    $[2] = threads.botThreadRows;
    $[3] = threads.humanThreadRows;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== fixAll.description || $[6] !== fixAll.isProviderResolved || $[7] !== fixAll.isSubmitting || $[8] !== fixAll.label || $[9] !== fixAll.run || $[10] !== fixAll.state || $[11] !== goToComment || $[12] !== threads.botThreadRows || $[13] !== threads.humanThreadRows) {
    t2 = [fixAll.description, fixAll.isProviderResolved, fixAll.isSubmitting, fixAll.label, fixAll.run, fixAll.state, goToComment, threads.botThreadRows, threads.humanThreadRows];
    $[5] = fixAll.description;
    $[6] = fixAll.isProviderResolved;
    $[7] = fixAll.isSubmitting;
    $[8] = fixAll.label;
    $[9] = fixAll.run;
    $[10] = fixAll.state;
    $[11] = goToComment;
    $[12] = threads.botThreadRows;
    $[13] = threads.humanThreadRows;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  useRegisterCommands(t1, t2);
}
function _temp3(row_1) {
  return row_1.status === "open";
}
function _temp2() {
  return false;
}
function _temp(row) {
  return isDisplayedCubicLogin(row.userLogin);
}